import { motion } from "framer-motion";
import { Zap, Award, Gauge, Sparkles } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 16 — Section 4 opener · Video Creation · "the trade-off is now gone".
 * Three-pillar reveal: SPEED · QUALITY · EFFICIENCY all lit at once.
 */

const pillars = [
  {
    label: "Speed",
    icon: Zap,
    color: "#96C11F",
    note: "Output in minutes, not days",
  },
  {
    label: "Quality",
    icon: Award,
    color: "#00deb6",
    note: "Cinematic, on-brand, every time",
  },
  {
    label: "Efficiency",
    icon: Gauge,
    color: "#e9a820",
    note: "Less effort per asset, infinitely",
  },
];

export default function Scene16_TradeoffGone() {
  return (
    <SceneShell variant="forest" align="left" data-testid="scene-tradeoff-gone">
      {/* Slow ambient backlight that brightens as the section "lands" */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.3 }}
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(150,193,31,0.16) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto w-full">
        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-5xl sm:text-7xl lg:text-[120px] leading-[1.05] pb-2"
          style={{
            background: "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
          data-testid="tradeoff-headline-1"
        >
          Video Editing.
        </motion.h2>

        {/* Three pillars lit up together */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 32, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.9,
                  delay: 2.4 + i * 0.22,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="relative rounded-2xl glass p-7 overflow-hidden"
                data-testid={`tradeoff-pillar-${i}`}
              >
                {/* Pulsing colour glow */}
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  animate={{ opacity: [0.5, 0.95, 0.5] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.5,
                  }}
                  style={{
                    background: `radial-gradient(ellipse at center, ${p.color}38 0%, transparent 70%)`,
                  }}
                />
                <div className="relative">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{
                      background: `${p.color}20`,
                      color: p.color,
                      border: `1px solid ${p.color}55`,
                    }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div
                    className="kn-display text-3xl sm:text-4xl"
                    style={{ color: p.color, fontWeight: 800 }}
                  >
                    {p.label}.
                  </div>
                  <div
                    className="mt-2 text-sm"
                    style={{ color: "rgba(255,255,255,0.65)" }}
                  >
                    {p.note}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.55 }}
          transition={{ delay: 3.6, duration: 0.8 }}
          className="mt-10 inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-white/55"
        >
          <Sparkles className="w-3.5 h-3.5" />
          All three · at the same time
        </motion.div>
      </div>
    </SceneShell>
  );
}
